import { PropsWithChildren, useEffect, useState } from 'react'
import { EventBusContextProvider } from '../../core'
import { ChildEventBusOptions, EventBus } from '../events'
import { useEventBus } from '../hooks'

export interface EventBusProviderProps<EventType> {
  value: EventBus<EventType>
}

export const EventBusProvider = <EventType,>({
  children,
  value,
}: PropsWithChildren<EventBusProviderProps<EventType>>) => (
  <EventBusContextProvider value={value as EventBus<unknown>}>
    {children}
  </EventBusContextProvider>
)

export type EventBusScopeProps<EventType> = ChildEventBusOptions<EventType>

/**
 * How the buffering process works for the example:
 *
 * 1) EventBusScope provides a ChildEventBus to ComponentA and ComponentB.
 * 2) useEffect is called in ComponentA and ComponentB which is used
 * by useEventBus to call eventbus.addListener(...).
 * 3) Because ChildEventBus is buffering events, every child component
 * receives all events that have been sent so far, so they don't miss
 * anything if they are not mounted in a specific order and another
 * component has emitted an event on mount.
 * 4) After useEffect is called in all of the rendered children useEffect
 * is called in EventBusScope which tells the ChildEventBus that it
 * can stop buffering events.
 *
 * This means that if a component is mounted later it will not receive
 * buffered events.
 *
 * @example
 * <EventBusScope>
 *   <div>
 *     <ComponentA />
 *   </div>
 *   <ComponentB />
 * </EventBusScope>
 */
export const EventBusScope = <EventType,>({
  children,
  bufferEvents = true,
  closed = false,
  allowedEvents,
}: PropsWithChildren<EventBusScopeProps<EventType>>) => {
  const eventbus = useEventBus<EventType>()
  const [childEventbus, setChildEventbus] = useState(() =>
    eventbus.createChild({ bufferEvents, closed, allowedEvents }),
  )

  useEffect(() => {
    if (childEventbus.parent === eventbus) {
      // All rendered children should have called useEffect now and connected
      // their eventbus listeners (react calls useEffect in children before the
      // parent)
      childEventbus.stopBuffering()
    } else {
      childEventbus.dispose()
      setChildEventbus(eventbus.createChild({ bufferEvents, closed }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setChildEventbus doesn't change, and we only care about bufferEvents on init, so they don't need to be deps
  }, [eventbus])

  return <EventBusProvider value={childEventbus}>{children}</EventBusProvider>
}
